import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticatedFetch } from './utils/api';
import Dashboard from './components/Dashboard';
import ProductsPage from './components/ProductsPage';
import OptimizedImagesPage from './components/OptimizedImagesPage';
import SettingsPage from './components/SettingsPage';
import OnboardingPage from './components/OnboardingPage';
import PricingPage from './components/PricingPage';
import ManualUploadPage from './components/ManualUploadPage';
import ImageCrushPage from './components/ImageCrushPage';
import ThemeCrushPage from './components/ThemeCrushPage';
import './app.css';

// Conditionally export App Bridge instance
// Uses window.shopify (initialized in index.js) when available
// Returns null on public routes where App Bridge is not initialized
// Components should check if it's null before using (optional usage)
let shopifyAppBridge = null;

if (typeof window !== 'undefined' && window.shopify) {
  shopifyAppBridge = window.shopify;
}

export { shopifyAppBridge };

// Inner component that uses router hooks
function AppContent() {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [initializing, setInitializing] = useState(true);
  const [shop, setShop] = useState(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null); // null = checking, true = completed, false = not completed
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedOnboardingRef = useRef(false); // Prevent multiple checks

  // Initialize app - get shop and host params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');
    const hostParam = urlParams.get('host');
    const subscriptionConfirmed = urlParams.get('subscription') === 'confirmed';

    if (!shopParam) {
      console.error('No shop parameter found');
      setInitializing(false);
      return;
    }

    setShop(shopParam);

    // If no host parameter, redirect to OAuth immediately
    if (!hostParam) {
      window.location.href = `/login?shop=${shopParam}`;
      return;
    }

    // If returning from subscription confirmation, mark onboarding as completed
    // and navigate to dashboard
    if (subscriptionConfirmed) {
      setOnboardingCompleted(true);
      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
      // Navigate to dashboard
      navigate('/', { replace: true });
    }

    // If returning from a successful one-time credit purchase, navigate to pricing
    // so PricingPage can show the success toast and refresh the balance
    if (urlParams.get('credits_added') === '1') {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('credits_added');
      window.history.replaceState({}, '', newUrl.toString());
      navigate('/pricing?credits_added=1', { replace: true });
    }

    // If returning from a declined/cancelled credit purchase, navigate to pricing
    // so PricingPage can show the declined banner with a retry option
    if (urlParams.get('credits_declined') === '1') {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('credits_declined');
      window.history.replaceState({}, '', newUrl.toString());
      navigate('/pricing?credits_declined=1', { replace: true });
    }

    // We have host, so we're authenticated
    // Set a timeout fallback - if onboarding check doesn't complete within 15 seconds,
    // assume onboarding not completed and show onboarding page
    const timeout = setTimeout(() => {
      setOnboardingCompleted((prev) => {
        if (prev === null) {
          setInitializing(false);
          return false;
        }
        return prev;
      });
    }, 15000);

    return () => {
      clearTimeout(timeout);
    };
  }, []); // Only run once on mount

  // Check onboarding status when authenticatedFetch becomes available
  useEffect(() => {
    // Only check if we have shop and authenticatedFetch, and haven't checked yet
    if (!shop || !authenticatedFetch || onboardingCompleted !== null || hasCheckedOnboardingRef.current) {
      return;
    }

    // Mark as checked to prevent multiple simultaneous checks
    hasCheckedOnboardingRef.current = true;

    let isMounted = true;

    // Try to fetch onboarding status; retries once after 2.5 s on failure so
    // a transient App Bridge auth delay doesn't incorrectly show the onboarding page.
    const checkOnboardingStatus = async (isRetry = false) => {
      try {
        const response = await authenticatedFetch('/api/subscription/onboarding/status');

        if (!isMounted) return;

        if (response && response.ok) {
          const result = await response.json();
          if (result.success) {
            const isCompleted = result.data?.isCompleted === true;
            if (isMounted) {
              setOnboardingCompleted(isCompleted);
              setInitializing(false);
            }
          } else {
            if (isRetry) {
              // Second attempt also failed — show onboarding as fallback
              if (isMounted) { setOnboardingCompleted(false); setInitializing(false); }
            } else {
              // First failure — wait and retry once
              setTimeout(() => { if (isMounted) checkOnboardingStatus(true); }, 2500);
            }
          }
        } else {
          if (isRetry) {
            if (isMounted) { setOnboardingCompleted(false); setInitializing(false); }
          } else {
            setTimeout(() => { if (isMounted) checkOnboardingStatus(true); }, 2500);
          }
        }
      } catch (error) {
        console.error('[App] Error checking onboarding status:', error);
        if (isRetry) {
          if (isMounted) { setOnboardingCompleted(false); setInitializing(false); }
        } else {
          // Retry once after delay — avoids showing onboarding due to transient auth errors
          setTimeout(() => { if (isMounted) checkOnboardingStatus(true); }, 2500);
        }
      }
    };

    checkOnboardingStatus();

    return () => {
      isMounted = false;
    };
  }, [shop, authenticatedFetch]); // Only depend on shop and authenticatedFetch

  // Listen for onboarding completion event
  useEffect(() => {
    const handleOnboardingCompleted = () => {
      // Immediately update state to show dashboard
      setOnboardingCompleted(true);
    };

    window.addEventListener('onboardingCompleted', handleOnboardingCompleted);
    return () => {
      window.removeEventListener('onboardingCompleted', handleOnboardingCompleted);
    };
  }, []);

  // Re-check onboarding status when navigating to '/' after onboarding was false
  // This handles the case when user completes onboarding and navigates to dashboard
  const recheckOnboardingRef = useRef(false);
  useEffect(() => {
    // Only re-check if we're on '/' and onboarding was false
    if (location.pathname !== '/' || onboardingCompleted !== false) {
      // Reset re-check flag when not on '/' or onboarding is not false
      if (location.pathname !== '/') {
        recheckOnboardingRef.current = false;
      }
      return;
    }

    // Only proceed if we have shop and authenticatedFetch, and haven't re-checked yet
    if (!shop || !authenticatedFetch || recheckOnboardingRef.current) {
      return;
    }

    // Mark as re-checked to prevent multiple simultaneous re-checks
    recheckOnboardingRef.current = true;
    
    const recheckOnboarding = async () => {
      try {
        const response = await authenticatedFetch('/api/subscription/onboarding/status');
        if (response && response.ok) {
          const result = await response.json();
          if (result.success) {
            const isCompleted = result.data?.isCompleted === true;
            if (isCompleted) {
              setOnboardingCompleted(true);
            }
          }
        }
      } catch (error) {
        console.error('[App] Error re-checking onboarding status:', error);
      }
    };
    
    // Small delay to ensure backend has updated
    setTimeout(() => {
      recheckOnboarding();
    }, 300);
  }, [location.pathname, shop, authenticatedFetch]); // Removed onboardingCompleted from deps to prevent loops

    // Show nothing until onboarding status is known (avoids flash of loading UI)
  if (initializing || onboardingCompleted === null) {
    return null;
  }

  // If no shop, show error
  if (!shop) {
    return (
      <s-page heading="Shopify Image Optimizer">
        <s-section>
          <s-banner tone="critical">
            No shop parameter found. Please access this app through Shopify Admin.
          </s-banner>
        </s-section>
      </s-page>
    );
  }

  // Now we know onboarding status - render accordingly
  // If onboarding not completed, show onboarding page directly (no Routes to prevent flashing)
  // If onboarding completed, show main app with routes
  if (onboardingCompleted === false) {
    // Show onboarding page directly - no navigation, no page wrapper, no Routes
    return <OnboardingPage />;
  }

  // Onboarding completed - show main app with navigation
  return (
    <>
      {/* Shopify App Navigation */}
      <s-app-nav>
        <a href="/" rel="home" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
          Dashboard
        </a>
        {/* Unoptimized Products, Optimized Products and Manual Upload hidden — use Image Crush instead */}
        {/* <a href="/products" onClick={(e) => { e.preventDefault(); navigate('/products'); }}>
          Unoptimized Products
        </a> */}
        {/* <a href="/optimized-images" onClick={(e) => { e.preventDefault(); navigate('/optimized-images'); }}>
          Optimized Products
        </a> */}
        {/* <a href="/manual-upload" onClick={(e) => { e.preventDefault(); navigate('/manual-upload'); }}>
          Manual Upload
        </a> */}
        <a href="/image-crush" onClick={(e) => { e.preventDefault(); navigate('/image-crush'); }}>
          Product & Manual Crush
        </a>
        <a href="/theme-crush" onClick={(e) => { e.preventDefault(); navigate('/theme-crush'); }}>
          Theme Crush
        </a>
        <a href="/settings" onClick={(e) => { e.preventDefault(); navigate('/settings'); }}>
          Settings
        </a>
        <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>
          Pricing
        </a>
      </s-app-nav>

      {/* Main app pages */}
      <s-page inlineSize="large" heading="Shopify Image Optimizer">
        <s-section paddingBlockStart="base">
          <s-stack gap="base">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              {/* Routes kept but pages not linked in nav — accessible via direct URL only */}
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/optimized-images" element={<OptimizedImagesPage />} />
              <Route path="/manual-upload" element={<ManualUploadPage />} />
              <Route path="/image-crush" element={<ImageCrushPage />} />
              <Route path="/theme-crush" element={<ThemeCrushPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              {/* Catch-all route - redirect to dashboard */}
              <Route path="*" element={<Dashboard />} />
            </Routes>
          </s-stack>
        </s-section>
      </s-page>
    </>
  );
}

// Main App component with BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
